import React, { useEffect, useRef } from 'react';
import { Zap, Lock, AlertCircle, Gem, Star } from 'lucide-react';
import gsap from 'gsap';
import { getMissionData } from '../logic/missionLogic';
import HUD from './HUD';
import BackButton from './BackButton';

function Missions({ app }) {
  const missionsRef = useRef(null);
  const missionData = getMissionData();

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === 'Escape') {
        app.goToMenu();
      }
    };
    window.addEventListener('keydown', handleEsc);

    gsap.from(missionsRef.current.children, {
      y: 100,
      opacity: 0,
      duration: 0.5,
      stagger: 0.2,
      ease: "back.out(1.4)"
    });

    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, []);

  const checkRequirements = (mission) => {
    if (mission.locked) return false;
    return mission.requirements.maxStat.current >= mission.requirements.maxStat.value &&
           mission.requirements.totalStat.current >= mission.requirements.totalStat.value;
  };

  const checkEnergy = (energyCost) => {
    return missionData.playerEnergy >= energyCost;
  };

  const getMaxEmbarks = (energyCost) => {
    return Math.floor(missionData.playerEnergy / energyCost);
  };

  return (
    <div className="bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#1a1a2e] h-full w-full mx-auto relative overflow-hidden text-white">
      <style>
        {`
          .hide-scrollbar::-webkit-scrollbar {
            display: none;
          }
          .hide-scrollbar {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
          @keyframes alertFlash {
            0%, 100% { opacity: 1; filter: drop-shadow(0 0 8px rgba(248, 113, 113, 0.8)); }
            50% { opacity: 0.5; filter: drop-shadow(0 0 2px rgba(248, 113, 113, 0.4)); }
          }
          .alert-flash {
            animation: alertFlash 1.5s infinite;
          }
        `}
      </style>
      <div className="absolute inset-0 bg-[url('/assets/nine-realms.png')] opacity-5 bg-cover bg-center" />
      
      <div className="p-6 flex flex-col h-full relative z-10">
        <div ref={missionsRef} className="flex flex-col gap-6 overflow-y-auto hide-scrollbar flex-1">
          {missionData.missions.map((mission, index) => (
            <div key={index} className={`relative bg-gradient-to-r from-blue-900/30 to-purple-900/30 rounded-xl overflow-hidden border border-white/10 backdrop-blur-sm min-h-[480px] ${mission.locked ? 'opacity-80' : ''}`}>
              <div className="absolute inset-0 z-0">
                <div 
                  className="absolute inset-0 bg-cover bg-center"
                  style={{
                    backgroundImage: `url(${mission.image})`,
                    maskImage: 'linear-gradient(to right, transparent, transparent 50%, black)',
                    WebkitMaskImage: 'linear-gradient(to right, transparent, transparent 50%, black)',
                    opacity: 0.3
                  }}
                />
              </div>
              {mission.locked && (
                <div className="absolute inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-20">
                  <div className="text-center">
                    <Lock size={32} className="mx-auto mb-2 text-red-400" />
                    <p className="text-red-200 font-bold">{mission.lockReason}</p>
                  </div>
                </div>
              )}
              <div className="p-8 relative z-10">
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Norse' }}>{mission.name}</h3>
                    <p className="text-gray-300 text-sm mb-4">{mission.description}</p>
                    <div className="flex gap-3 items-center">
                      {mission.requirements.maxStat.current >= mission.requirements.maxStat.value ? (
                        <div className="bg-white/10 px-4 py-2 rounded-lg text-base text-gray-200 flex flex-col">
                          <span>Max {mission.requirements.maxStat.type}</span>
                          <span className="font-bold">{mission.requirements.maxStat.current}</span>
                        </div>
                      ) : (
                        <div className="bg-white/10 px-4 py-2 rounded-lg text-base text-gray-200 flex flex-col">
                          <div className="flex items-center gap-2">
                            <AlertCircle size={20} className="text-red-400 alert-flash" />
                            <span>Max {mission.requirements.maxStat.type}</span>
                          </div>
                          <span className="font-bold">{mission.requirements.maxStat.value}</span>
                        </div>
                      )}
                      {mission.requirements.totalStat.current >= mission.requirements.totalStat.value ? (
                        <div className="bg-white/10 px-4 py-2 rounded-lg text-base text-gray-200 flex flex-col">
                          <span>Total {mission.requirements.totalStat.type}</span>
                          <span className="font-bold">{mission.requirements.totalStat.current}</span>
                        </div>
                      ) : (
                        <div className="bg-white/10 px-4 py-2 rounded-lg text-base text-gray-200 flex flex-col">
                          <div className="flex items-center gap-2">
                            <AlertCircle size={20} className="text-red-400 alert-flash" />
                            <span>Total {mission.requirements.totalStat.type}</span>
                          </div>
                          <span className="font-bold">{mission.requirements.totalStat.value}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="bg-black/20 rounded-lg p-6 mb-16">
                  <div className="flex gap-3">
                    {mission.rewards.map((reward, i) => (
                      <div key={i} className="bg-white/10 px-4 py-2 rounded-lg text-base flex items-center gap-2">
                        {reward.type === 'gems' ? <Gem size={20} className="text-blue-400" /> : <Star size={20} className="text-purple-400" />}
                        <span className="font-bold">{reward.amount}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex justify-end gap-2">
                  <button
                    disabled={!checkRequirements(mission) || !checkEnergy(mission.energyCost)}
                    className={`px-8 py-3 rounded-full font-bold text-center transition-all shadow-lg flex items-center gap-2 ${
                      checkRequirements(mission) && checkEnergy(mission.energyCost)
                        ? 'bg-white text-black hover:bg-gray-100'
                        : 'bg-gray-500/30 text-gray-400 cursor-not-allowed opacity-50'
                    }`}
                    style={{ fontFamily: 'Norse' }}
                  >
                    <span>Embark</span>
                    <div className="flex items-center gap-1 border-l border-current pl-2 ml-2 opacity-50">
                      <Zap size={16} className="text-yellow-600/50" />
                      <span>{mission.energyCost}</span>
                    </div>
                  </button>
                  {checkRequirements(mission) && getMaxEmbarks(mission.energyCost) > 1 && (
                    <button
                      className="px-8 py-3 rounded-full font-bold text-center transition-all shadow-lg flex items-center gap-2 bg-white text-black hover:bg-gray-100"
                      style={{ fontFamily: 'Norse' }}
                    >
                      <span>Embark x{getMaxEmbarks(mission.energyCost)}</span>
                      <div className="flex items-center gap-1 border-l border-current pl-2 ml-2 opacity-50">
                        <Zap size={16} className="text-yellow-400/50" />
                        <span>{mission.energyCost * getMaxEmbarks(mission.energyCost)}</span>
                      </div>
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        <BackButton onClick={() => app.goToMenu()} />
      </div>
    </div>
  );
}

export default Missions;
